// Lots techniques par logement — le modèle de Sorin et Marius (relecture du
// devis Martin, 12/10/2026).
//
// Un immeuble ne se chiffre pas au point lumineux ni au mètre de tube : les
// réseaux se vendent par logement, et ce qui les dessert par niveau ou par
// bâtiment. Chaque réseau d'un logement a un état — absent (à créer), à
// refaire (vétuste), ou existant conforme (rien à faire) — et le moteur en
// déduit les lignes et leurs quantités. Les prix restent ceux de la grille
// (unités « logement », « niveau », « m² », « u », « forfait »), donc
// modifiables dans /pro/referentiel sans toucher au code.
//
// Ce qui grandit avec le bien :
// - plus de logements → les postes « logement » se multiplient ;
// - des logements plus grands → la composante au m² habitable (points, prises,
//   circuits) et le nombre de radiateurs (un par ~12 m²) suivent la surface ;
// - plus de niveaux → les colonnes montantes et l'électricité des communs
//   suivent le nombre de niveaux desservis ;
// - une PAC commune → une base pour le premier logement, puis un circuit,
//   un circulateur et de la puissance par logement supplémentaire.
//
// Module pur, sans import : les règles (bases.json → reseaux) et la géométrie
// sont injectées par l'appelant ; un script Node peut le rejouer.

export const ETATS_RESEAU = [
  { id: 'absent',    label: 'Absent — à créer' },
  { id: 'a_refaire', label: 'Vétuste — à refaire' },
  { id: 'ok',        label: 'Existant, conforme' },
];

export const MODES_CHAUFFAGE = [
  { id: 'pac',        label: 'PAC air/eau commune + radiateurs eau' },
  { id: 'electrique', label: 'Radiateurs électriques par logement' },
  { id: 'aucun',      label: 'Hors devis' },
];

// Les lignes à l'unité ou au mètre que les forfaits par logement remplacent :
// une fois les réseaux générés, les garder dans les pièces compterait deux fois
// (le point lumineux ET l'installation du logement). Proposées au retrait, pas
// retirées d'office — une prise ajoutée dans une cuisine conservée reste légitime.
export const LIGNES_REMPLACEES = {
  electricite: ['electricite.point_lumineux', 'electricite.prise_simple', 'electricite.circuit_electrique', 'electricite.tableau_neuf', 'electricite.mise_aux_normes_elec', 'electricite.vmc_simple_flux', 'electricite.interphone_3log'],
  plomberie: ['plomberie.reseau_sdb', 'plomberie.evacuation_sdb', 'plomberie.alimentation_multicouche', 'plomberie.groupe_securite', 'plomberie.securisation_chauffe_eau'],
  chauffage: ['chauffage.radiateur_eau', 'chauffage.robinet_thermostatique', 'chauffage.pac_air_eau', 'chauffage.radiateurs_electriques_logement'],
};

/** Nom du logement pour un niveau habitable. */
export const NOM_LOGEMENT = (niveau) => `Logement ${niveau === 'Rez-de-chaussée' ? 'RDC' : niveau}`;
export const RESEAUX_PIECE = 'Réseaux & parties communes';   // ancien nom, une seule pièce pour tout
export const COMMUNS = 'Parties communes & bâtiment';
/** Nom de la pièce qui porte les installations techniques d'un logement. */
export const PIECE_TECHNIQUE = (nomLogement) => `Installations techniques — ${nomLogement}`;
/** Une pièce qui ne fait partie d'aucun logement (garage, cave, combles, local). */
export const estHorsLogement = (r) => r.type === 'facade' || PAS_UN_LOGEMENT.test(r.customLabel || r.label || '');

const PAS_UN_LOGEMENT = /garage|cave|cellier|grenier|comble|local|chaufferie|buanderie commune|cage|palier commun/i;

/**
 * Propose un logement par niveau habitable à partir des pièces dessinées : la
 * surface est la somme des pièces du niveau (hors garage, cave, local). C'est un
 * point de départ que le métreur corrige (fusionner un duplex, scinder un plateau).
 * @param rooms pièces du relevé (type, label, customLabel, niveau, vertices)
 * @param h { niveauDe(room) → libellé de niveau, ORDRE_NIVEAUX, polygonArea(vertices), habitables?: [niveaux] }
 * @param etatDefaut état des réseaux proposé (d'après l'état du bien)
 */
export function logementsDepuisPieces(rooms, { niveauDe, ORDRE_NIVEAUX, polygonArea, habitables = ['Rez-de-chaussée', '1er étage', '2e étage', '3e étage'] }, etatDefaut = 'a_refaire') {
  const parNiveau = new Map();
  for (const r of rooms || []) {
    const niveau = niveauDe(r);
    if (!habitables.includes(niveau)) continue;
    if (r.technique || r.type === 'facade' || PAS_UN_LOGEMENT.test(r.customLabel || r.label || '') || /^(Installations techniques|Réseaux & parties communes|Parties communes)/.test(r.customLabel || '')) continue;
    const v = Array.isArray(r.vertices) && r.vertices.length >= 3 ? r.vertices : [];
    const s = polygonArea(v);
    parNiveau.set(niveau, (parNiveau.get(niveau) || 0) + (Number.isFinite(s) ? s : 0));
  }
  return [...parNiveau.entries()]
    .sort((a, b) => ORDRE_NIVEAUX.indexOf(a[0]) - ORDRE_NIVEAUX.indexOf(b[0]))
    .map(([niveau, surface], i) => ({
      id: `log-${i + 1}`,
      nom: NOM_LOGEMENT(niveau),
      niveaux: [niveau],
      surface: Math.round(surface * 10) / 10,
      electricite: etatDefaut,
      plomberie: etatDefaut,
      chauffage: etatDefaut,
    }));
}

/** Les niveaux desservis par les réseaux communs : ceux des logements, dédoublonnés. */
export function niveauxDesservis(logements) {
  return [...new Set((logements || []).flatMap(l => l.niveaux || []))];
}

const aFaire = (etat) => etat === 'absent' || etat === 'a_refaire';
const nb = (n) => Math.round(Number(n) * 100) / 100;

/**
 * Puissance d'une PAC pour une surface chauffée : W/m² haut (bâti ancien),
 * arrondie au palier de gamme supérieur, jamais sous le mini ; au-delà du
 * dernier palier, par pas (cascade). Les paramètres viennent de bases.json
 * → reseaux.chauffage.pac ; des défauts raisonnables sinon.
 */
export function puissancePac(surfaceM2, regles = {}) {
  const wParM2 = Number(regles.wParM2) || 100;
  const kwMin = Number(regles.kwMin) || 6;
  const paliers = Array.isArray(regles.paliers) && regles.paliers.length ? regles.paliers : [6, 8, 11, 14, 16, 20, 24, 28, 32];
  const pas = Number(regles.pasAuDela) || 4;
  const besoin = Math.max(kwMin, (Number(surfaceM2) || 0) * wParM2 / 1000);
  const palier = paliers.find(p => p >= besoin);
  const kw = palier != null ? palier : Math.ceil(besoin / pas) * pas;
  return { kw, wParM2, besoin: Math.round(besoin * 10) / 10 };
}

/**
 * Les lignes de lots techniques déduites des logements.
 * @param {object} p
 * @param p.logements [{ nom, niveaux, surface, electricite, plomberie, chauffage }]
 * @param p.regles bases.json → reseaux
 * @param p.modeChauffage 'pac' | 'electrique' | 'aucun'
 * @param p.interphone bool — à partir de 2 logements par défaut
 * @returns [{ key, qty, note, auto: 'reseaux' }]
 */
export function reseauxDepuisLogements({ logements = [], regles, modeChauffage = 'pac', interphone = null }) {
  const R = regles || {};
  const lignes = [];
  // `groupe` : le logement que la ligne équipe (son nom), ou les parties communes.
  const push = (key, qty, note, groupe = COMMUNS) => { if (key && qty > 0) lignes.push({ key, qty: nb(qty), note, auto: 'reseaux', groupe }); };
  const niveaux = niveauxDesservis(logements);
  const plusieurs = logements.length > 1;
  const elec = logements.filter(l => aFaire(l.electricite));
  const plomb = logements.filter(l => aFaire(l.plomberie));
  const chauf = logements.filter(l => aFaire(l.chauffage));

  // ── Électricité : base par logement + points au m² ; communs par niveau ; terre par bâtiment
  const E = R.electricite || {};
  for (const l of elec) {
    push(E.logement, 1, 'tableau, mise aux normes, Consuel', l.nom);
    push(E.m2, l.surface, `${l.surface} m² habitables`, l.nom);
    push(E.vmc, 1, '', l.nom);
  }
  if (elec.length) {
    if (plusieurs) push(E.niveau, niveaux.length, `cage d'escalier et communs, ${niveaux.length} niveau${niveaux.length > 1 ? 'x' : ''}`);
    for (const k of E.batiment || []) push(k, 1, 'bâtiment');
  }

  // ── Plomberie : distribution et évacuations par logement ; colonnes par niveau ; mise en eau
  const P = R.plomberie || {};
  for (const l of plomb) for (const k of [].concat(P.logement || [])) push(k, 1, '', l.nom);
  if (plomb.length) {
    if (plusieurs || plomb.some(l => l.plomberie === 'absent')) push(P.niveau, niveaux.length, `colonnes montantes, ${niveaux.length} niveau${niveaux.length > 1 ? 'x' : ''}`);
    for (const k of P.batiment || []) push(k, 1, 'bâtiment');
  }

  // ── Chauffage : réseau et radiateurs par logement ; PAC commune dimensionnée à la surface chauffée
  const C = R.chauffage || {};
  if (modeChauffage === 'pac' && chauf.length) {
    for (const l of chauf) {
      push(C.logement, 1, 'tuyauterie, hors radiateurs', l.nom);
      const radiateurs = Math.max(1, Math.ceil(l.surface / (R.m2ParRadiateur || 12)));
      push(C.radiateur, radiateurs, `un radiateur par ~${R.m2ParRadiateur || 12} m²`, l.nom);
      push(C.robinet, radiateurs, '', l.nom);
    }
    const surface = chauf.reduce((s, l) => s + (Number(l.surface) || 0), 0);
    const { kw, wParM2 } = puissancePac(surface, C.pac);
    if (C.pac && C.pac.base) {
      // Une PAC n'est pas un forfait : 40 m² et 300 m² ne demandent pas la même
      // machine. Base (pose, ballon tampon, mise en service) + puissance au kW,
      // dimensionnée haut (100 W/m² : un bâti ancien sans ITE), arrondie au
      // palier de gamme supérieur ; au-delà de la gamme, en cascade.
      push(C.pac.base, 1, `PAC commune — pose, ballon tampon, mise en service (${chauf.length} logement${chauf.length > 1 ? 's' : ''}, ${Math.round(surface)} m² chauffés)`);
      push(C.pac.parKw, kw, `puissance ${kw} kW pour ${Math.round(surface)} m² à ${wParM2} W/m²`);
    } else {
      push(C.pacUnite || C.pac, 1, `PAC commune — base pour ${chauf[0].nom}`);
    }
    if (chauf.length > 1) push(C.pacCircuitSup, chauf.length - 1, `${chauf.length - 1} logement${chauf.length > 2 ? 's' : ''} supplémentaire${chauf.length > 2 ? 's' : ''} sur la PAC — circuit, circulateur, régulation`);
  } else if (modeChauffage === 'electrique') {
    for (const l of chauf) push(C.electrique, 1, '', l.nom);
  }

  // ── Communs : interphone dès 2 logements, nettoyage par logement
  const K = R.communs || {};
  const avecInterphone = interphone == null ? plusieurs : interphone;
  if (avecInterphone && logements.length) {
    push(K.interphone, 1, `${logements.length} logement${plusieurs ? 's' : ''}`);
    const sup = logements.length - (K.interphoneInclus || 3);
    if (sup > 0) push(K.interphonePosteSup, sup, 'postes au-delà de 3');
  }
  for (const l of logements) push(K.nettoyage, 1, '', l.nom);

  return lignes;
}

// ── Préparation d'un relevé : ce que le métreur n'a plus à faire à la main ──
// (audit du deviseur, 12/10/2026). Fonctions pures : la page fournit les prix.

const ENEDIS = /compteur|enedis|branchement|coffret de branchement/i;

/**
 * Nettoie les lignes d'un relevé :
 * - une même clé une seule fois par pièce (surface : une ligne « auto » ;
 *   unité ou forfait : quantités additionnées) ;
 * - un poste englobant retire partout ceux qu'il contient (bases.json → exclusifs) ;
 * - les travaux du gestionnaire de réseau (compteur, branchement) sortent du
 *   devis : ils sont « en sus » et rapportés dans `exclus`.
 * @param rooms pièces du relevé
 * @param o.regles bases.json (exclusifs)
 * @param o.uniteDe (key) → unité de la grille
 * @param o.libelleDe (key) → libellé de la grille
 */
export function nettoyerLignes(rooms, { regles = {}, uniteDe = () => '', libelleDe = () => '' } = {}) {
  const exclus = [];
  const retirees = [];
  const surface = (k) => ['m²', 'm2', 'ml'].includes(uniteDe(k));
  let rs = (rooms || []).map(r => {
    const vues = new Map();
    const lines = [];
    for (const l of r.lines || []) {
      if (ENEDIS.test(l.key) || ENEDIS.test(libelleDe(l.key))) { exclus.push(libelleDe(l.key) || l.key); continue; }
      const deja = vues.get(l.key);
      if (deja != null) {
        const a = lines[deja];
        lines[deja] = surface(l.key) ? { ...a, qty: (a.qty === 1 || l.qty === 1) ? 1 : Math.max(a.qty, l.qty) } : { ...a, qty: (Number(a.qty) || 0) + (Number(l.qty) || 0) };
        retirees.push(`${r.customLabel || r.label} · ${libelleDe(l.key) || l.key} (doublon)`);
        continue;
      }
      vues.set(l.key, lines.length);
      lines.push(l);
    }
    return { ...r, lines };
  });
  const presentes = new Set(rs.flatMap(r => r.lines.map(l => l.key)));
  const aRetirer = new Map();
  for (const [garder, contenus] of Object.entries(regles.exclusifs || {})) {
    if (garder.startsWith('_') || !presentes.has(garder)) continue;
    for (const k of contenus) aRetirer.set(k, garder);
  }
  if (aRetirer.size) {
    rs = rs.map(r => ({ ...r, lines: r.lines.filter(l => {
      if (!aRetirer.has(l.key)) return true;
      retirees.push(`${r.customLabel || r.label} · ${libelleDe(l.key) || l.key} (compris dans « ${libelleDe(aRetirer.get(l.key)) || aRetirer.get(l.key)} »)`);
      return false;
    }) }));
  }
  return { rooms: rs, exclus: [...new Set(exclus)], retirees };
}

const RESEAU_DE = (key) => Object.entries(LIGNES_REMPLACEES).find(([, ks]) => ks.includes(key))?.[0] || null;

/**
 * Logements et lots techniques posés d'office quand le relevé le justifie :
 * plusieurs logements, ou un réseau touché dans au moins la moitié des pièces
 * habitables (≥ 3). Un réseau que l'analyse n'a pas touché reste « conforme ».
 * Les lignes à l'unité que les forfaits remplacent sortent des pièces.
 * @returns { rooms, logements, genere: bool }
 */
export function poserReseaux(rooms, { regles, modeChauffage = 'pac', niveauDe, ORDRE_NIVEAUX, polygonArea, existe = () => true, idBase = null, rectVertices, wallHeight = 2.5 }) {
  // Des ids neufs, jamais ceux des pièces déjà là (construites dans la même milliseconde).
  const base = idBase ?? Math.max(Date.now(), ...rooms.map(r => Number(r.id) || 0)) + 1;
  const logements = logementsDepuisPieces(rooms, { niveauDe, ORDRE_NIVEAUX, polygonArea }, 'ok');
  if (!logements.length) return { rooms, logements: [], genere: false };
  const habitables = rooms.filter(r => !estHorsLogement(r) && !r.technique && (r.customLabel || r.label) !== 'Ensemble du bien');
  const touche = { electricite: 0, plomberie: 0, chauffage: 0 };
  for (const r of rooms) {
    const vus = new Set((r.lines || []).map(l => RESEAU_DE(l.key)).filter(Boolean));
    vus.forEach(k => { touche[k]++; });
  }
  const seuil = Math.max(3, Math.ceil(habitables.length / 2));
  const reseauxAFaire = Object.keys(touche).filter(k => touche[k] > 0 && (logements.length > 1 || touche[k] >= seuil));
  if (!reseauxAFaire.length) return { rooms, logements, genere: false };
  const lgs = logements.map(l => ({ ...l, ...Object.fromEntries(reseauxAFaire.map(k => [k, 'a_refaire'])) }));
  const lignes = reseauxDepuisLogements({ logements: lgs, regles: regles.reseaux, modeChauffage }).filter(l => existe(l.key));
  const remplacees = new Set(reseauxAFaire.flatMap(k => LIGNES_REMPLACEES[k]));
  const nettoyage = regles.reseaux?.communs?.nettoyage;
  let rs = rooms.map(r => ({ ...r, lines: (r.lines || []).filter(l => !remplacees.has(l.key) && l.key !== nettoyage) }));
  const groupes = [...new Set(lignes.map(l => l.groupe))];
  groupes.forEach((g, i) => {
    const lg = lgs.find(l => l.nom === g);
    rs.push({
      id: base + i, type: 'autre', label: 'Autre pièce', customLabel: g === COMMUNS ? COMMUNS : PIECE_TECHNIQUE(g), technique: g,
      vertices: rectVertices ? rectVertices(3, 3) : [], walls: [], height: String(wallHeight), addons: [], photos: [], valide: false,
      notes: 'Installations techniques déduites des logements — régénérer après un changement des logements.',
      niveau: lg?.niveaux?.[0] || null,
      lines: lignes.filter(l => l.groupe === g).map(l => ({ key: l.key, qty: l.qty, priceOverride: null, auto: 'reseaux', note: l.note })),
    });
  });
  return { rooms: rs, logements: lgs, genere: true };
}
